import React from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { Box, Typography, Grid, Card } from "@mui/material";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// RAM chart data and options
const ramChartData = {
  labels: ["Used (8.1 GiB)", "Available (116 GiB)"],
  datasets: [
    {
      data: [8.1, 116],
      backgroundColor: ["#ff6666", "#66cc66"],
      hoverOffset: 4,
    },
  ],
};

const ramChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
};

// Disk usage data and options
const diskLabels = ["/", "/boot", "/boot/efi", "/run", "/dev/shm", "/run/lock", "/run/user/0"];
const diskTotal = [14, 2.0, 0.256, 13, 63, 0.005, 13];
const diskUsed = [3.5, 0.255, 0.0003, 0.0017, 0.000384, 0, 0];

const diskChartData = {
  labels: diskLabels,
  datasets: [
    {
      label: "Total (GiB)",
      data: diskTotal,
      backgroundColor: "#cce5ff",
    },
    {
      label: "Used (GiB)",
      data: diskUsed,
      backgroundColor: "#3399ff",
    },
  ],
};

const diskChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: "GiB",
      },
    },
  },
};
const extactDigit = (str) => {
  if (!str) return {}
  const match = str.match(/[\d.]+/g);
  const digit = match ? match.join("").match(/^\d*\.?\d+/)?.[0] : "";
  const letter = str.replace(/[^a-zA-Z]/g, "");
  return { digit, letter }
}
function convertToTB(value) {
  if (!value) return
  const units = {
    K: 1 / (1024 ** 4),
    M: 1 / (1024 ** 3),
    G: 1 / (1024 ** 2),
    T: 1 / 1024,
    P: 1
  };

  const match = value.match(/^([\d.]+)([KMGTPE]?)/i);
  if (!match) return 0;
  const number = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  const factor = units[unit] || 1;
  return +(number * factor).toFixed(4); // TB value
}
function convertToGB(value) {
  if (!value) return 0;

  const units = {
    K: 1 / (1024 ** 2), // KB to GB
    M: 1 / 1024,        // MB to GB
    G: 1,               // GB to GB
    T: 1024,            // TB to GB
    P: 1024 * 1024      // PB to GB
  };

  const match = value.match(/^([\d.]+)([KMGTPE]?)/i);
  if (!match) return 0;
  const number = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  const factor = units[unit] || 1;
  return +(number * factor).toFixed(2); // GB value with 2 decimals
}
function convertToMB(value) {
  if (!value) return 0;

  const units = {
    K: 1 / 1024,          // KB to MB
    M: 1,                 // MB to MB
    G: 1024,              // GB to MB
    T: 1024 * 1024,       // TB to MB
    P: 1024 * 1024 * 1024 // PB to MB
  };

  const match = value.match(/^([\d.]+)([KMGTPE]?)/i);
  if (!match) return 0;

  const number = parseFloat(match[1]);
  const unit = match[2].toUpperCase();

  const factor = units[unit] || 1;
  return +(number * factor).toFixed(2); // MB value with 2 decimals
}



export default function SystemStatsCharts({ data }) {

  React.useState(() => {
    ramChartData.labels = [`Used (${data?.ram?.Mem?.used})`, `Avaialable (${data?.ram?.Mem?.available})`]
    const used = extactDigit(data?.ram?.Mem?.used)
    const available = extactDigit(data?.ram?.Mem?.available)
    ramChartData.datasets = [
      {
        data: [used.digit, available.digit],
        backgroundColor: ["#ff6666", "#66cc66"],
        hoverOffset: 4,
      },
    ]
    diskChartData.labels = (data?.disk_space || []).map((d) => d.Filesystem)
    diskChartData.datasets[0].data = (data?.disk_space || []).map((d) => convertToGB(d.Size))
    diskChartData.datasets[1].data = (data?.disk_space || []).map((d) => convertToGB(d.Used))
  }, [data])

  return (
    <Box>
      <Grid container spacing={2}>
        {/* RAM Pie Chart */}
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Card elevation={3} sx={{ p: 1 }}>
            <Typography gutterBottom>RAM Usage (Total: {data?.ram?.Mem?.total})</Typography>
            <Box sx={{ width: '100%', height: 200 }}>
              <Pie data={ramChartData} options={ramChartOptions} />
            </Box>
          </Card>
        </Grid>

        {/* Disk Bar Chart */}
        <Grid size={{ xs: 12, sm: 8, md: 8 }}>
          <Card elevation={3} sx={{ p: 1 }}>
            <Typography gutterBottom>Disk Usage by Mount Point</Typography>
            <Box sx={{ width: '100%', height: 200 }}>
              <Bar data={diskChartData} options={diskChartOptions} />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
